/**
 * One open streaming response, consumable exactly once.
 * Port of fastSDK's `StreamSession`. JavaScript is async-native, so no
 * thread/loop bridging is needed: the session wraps the fetch body's
 * ReadableStream and exposes async iterators.
 */

import { chunkText } from './sse';

const DONE = Symbol('sse-done');

export class StreamSession {
  readonly contentType: string;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private consumed = false;

  constructor(private readonly response: Response) {
    this.contentType = response.headers.get('content-type') ?? '';
  }

  /** True for `text/event-stream` responses (SSE-framed chunks). */
  get isSse(): boolean {
    return this.contentType.includes('text/event-stream');
  }

  /** Extract text from an SSE chunk (OpenAI delta JSON or plain string). */
  static chunkText = chunkText;

  /** Auto-select SSE chunks or raw bytes based on the content type. */
  [Symbol.asyncIterator](): AsyncIterator<any> {
    return this.isSse ? this.iterChunks() : this.iterBytes();
  }

  /** Yield decoded SSE items: parsed JSON objects or plain data strings. */
  async *iterChunks(): AsyncGenerator<any> {
    let buffer = '';
    for await (const bytes of this.iterBytes()) {
      buffer += new TextDecoder().decode(bytes, { stream: true });
      let newlineIndex: number;
      while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, newlineIndex);
        buffer = buffer.slice(newlineIndex + 1);
        const item = decodeSseLine(line);
        if (item === undefined) continue;
        if (item === DONE) return;
        yield item;
      }
    }
    const item = decodeSseLine(buffer);
    if (item !== undefined && item !== DONE) yield item;
  }

  /** Yield decoded SSE chunks as text (joins OpenAI deltas). */
  async *iterText(): AsyncGenerator<string> {
    for await (const chunk of this.iterChunks()) {
      const text = chunkText(chunk);
      if (text) yield text;
    }
  }

  /** Yield raw byte chunks of the response body. */
  async *iterBytes(): AsyncGenerator<Uint8Array> {
    if (this.consumed) throw new Error('StreamSession is already being consumed');
    this.consumed = true;
    if (!this.response.body) {
      const buffer = new Uint8Array(await this.response.arrayBuffer());
      if (buffer.length) yield buffer;
      return;
    }
    this.reader = this.response.body.getReader();
    try {
      while (true) {
        const { done, value } = await this.reader.read();
        if (done) return;
        if (value?.length) yield value;
      }
    } finally {
      this.reader.releaseLock();
      this.reader = null;
    }
  }

  /** Drain the remaining stream into one byte buffer. */
  async readAllBytes(): Promise<Uint8Array> {
    const chunks: Uint8Array[] = [];
    for await (const chunk of this.iterBytes()) chunks.push(chunk);
    const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      out.set(chunk, offset);
      offset += chunk.length;
    }
    return out;
  }

  /** Cancel the underlying response body. Idempotent. */
  async close(): Promise<void> {
    try {
      if (this.reader) {
        await this.reader.cancel();
      } else if (!this.consumed && this.response.body) {
        await this.response.body.cancel();
      }
    } catch {
      // teardown must never throw
    }
  }
}

/** Decode one SSE line: undefined = skip, DONE = end of stream. */
function decodeSseLine(line: string): any | typeof DONE | undefined {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith(':')) return undefined;
  if (!trimmed.startsWith('data:')) return trimmed;
  const data = trimmed.slice(5).trim();
  if (data === '[DONE]') return DONE;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}
